import React from "react";
import { observer } from "mobx-react-lite";

import type PaymentBankTransferViewModel from "viewModels/finance/payments/bankTransfer/paymentBankTransferViewModel";
import type { ViewComponent } from "@frui.ts/views";
import FilterView from "views/common/filter";

import { Input } from "@frui.ts/bootstrap";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import NumberRangeInputControl from "controls/numberRangeInput";
import DateRangeInputControl from "controls/dateRangeInput";
import { FormLabelWithTooltip } from "controls/formLabelWithTooltip";
import AdvancedInput from "@edaz/edazkit/controls/AdvancedInput";
import { IconType } from "@edaz/edazkit/Icon";
import Button from "@edaz/edazkit/Button";
import { isFilterDirty, isFilterEmpty } from "utils/filters";

const PaymentBankTransferFilters: ViewComponent<PaymentBankTransferViewModel> = observer(({ vm }) => {
  const regexOnlyNumbers = /[^0-9]+/g;
  const regexNumbersChars = /[^0-9A-Z-]+/g;
  // eslint-disable-next-line no-useless-escape
  const regexBankAcc = /[^0-9A-Z\/-]+/g;
  const regex = vm.filter.counterpartyBank?.length ? regexNumbersChars : regexBankAcc;
  const customFilterButton = (
    <Button iconLeft={IconType.Search} className="btn-form-height" type="submit" disabled={!isFilterDirty(vm.filter)}>
      {vm.translateFilter("customFilter")}
    </Button>
  );

  const customResetButton =
    isFilterDirty(vm.filter) || !isFilterEmpty(vm.filter) ? (
      <Button iconLeft={IconType.Cross} variant="outline-danger" className="btn-form-height" type="reset">
        {vm.translateFilter("customResetFilter")}
      </Button>
    ) : (
      <></>
    );

  return (
    <FilterView vm={vm} customResetButton={customResetButton} customFilterButton={customFilterButton}>
      <div className="px-2 row">
        <Form.Group as={Col}>
          <FormLabelWithTooltip tooltipText={vm.translateFilter("counterpartyAccountNumberTooltip")}>
            {vm.translateFilter("counterpartyAccountNumber")}
          </FormLabelWithTooltip>
          <AdvancedInput
            target={vm.filter}
            property="counterpartyAccountNumber"
            placeholder={vm.translatePlaceholder("counterpartyAccountNumber")}
            ignoredCharacters={regex}
          />
        </Form.Group>
        <Form.Group as={Col}>
          <FormLabelWithTooltip tooltipText={vm.translateFilter("counterpartyBankTooltip")}>
            {vm.translateFilter("counterpartyBank")}
          </FormLabelWithTooltip>
          <AdvancedInput
            target={vm.filter}
            property="counterpartyBank"
            disabled={vm.isDisabledBankCode}
            placeholder={vm.translatePlaceholder("counterpartyBank")}
            ignoredCharacters={/[^0-9A-Z]+/g}
          />
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>{vm.translateFilter("counterpartyName")}</Form.Label>
          <Input target={vm.filter} property="counterpartyName" placeholder={vm.translatePlaceholder("counterpartyName")} />
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>{vm.translateFilter("variableSymbol")}</Form.Label>
          <AdvancedInput
            target={vm.filter}
            property="variableSymbol"
            placeholder={vm.translatePlaceholder("variableSymbol")}
            ignoredCharacters={regexOnlyNumbers}
          />
        </Form.Group>
      </div>
      <div className="px-2 row">
        <Form.Group as={Col}>
          <Form.Label>{vm.translateFilter("specificSymbol")}</Form.Label>
          <AdvancedInput
            target={vm.filter}
            property="specificSymbol"
            placeholder={vm.translatePlaceholder("specificSymbol")}
            ignoredCharacters={regexOnlyNumbers}
          />
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>{vm.translateFilter("constantSymbol")}</Form.Label>
          <AdvancedInput
            target={vm.filter}
            property="constantSymbol"
            placeholder={vm.translatePlaceholder("constantSymbol")}
            ignoredCharacters={regexOnlyNumbers}
          />
        </Form.Group>
        <Form.Group as={Col} md={6}>
          <div className="d-flex flex-row">
            <div style={{ flex: "1" }}>
              <Form.Label>{vm.translateFilter("statementDate.from")}</Form.Label>
            </div>
            <div style={{ flex: "1", marginLeft: "30px" }}>
              <Form.Label>{vm.translateFilter("statementDate.to")}</Form.Label>
            </div>
          </div>
          <div>
            <DateRangeInputControl target={vm.filter} property="statementDate" customPlaceholder="paymentsStatementDate" />
          </div>
        </Form.Group>
      </div>
      <div className="px-2 row">
        <Form.Group as={Col} md={3}>
          <div className="d-flex flex-row">
            <div style={{ flex: "1" }}>
              <FormLabelWithTooltip tooltipText={vm.translateFilter("amountFromTooltip")}>
                {vm.translateFilter("amount.from")}
              </FormLabelWithTooltip>
            </div>
            <div style={{ flex: "1", marginLeft: "30px" }}>
              <Form.Label>{vm.translateFilter("amount.to")}</Form.Label>
            </div>
          </div>
          <div>
            <NumberRangeInputControl
              target={vm.filter}
              property="amount"
              customPlaceholder="paymentsAmount"
              step={0.01}
              min={0}
            />
          </div>
        </Form.Group>
        <Form.Group as={Col} md={3}>
          <Form.Label>{vm.translateFilter("messageForRecipient")}</Form.Label>
          <Input target={vm.filter} property="messageForRecipient" placeholder={vm.translatePlaceholder("messageForRecipient")} />
        </Form.Group>
        <Form.Group as={Col} md={3}>
          <Form.Label>{vm.translateFilter("additionalInformation")}</Form.Label>
          <Input
            target={vm.filter}
            property="additionalInformation"
            placeholder={vm.translatePlaceholder("additionalInformation")}
          />
        </Form.Group>

        <Form.Group as={Col} md={3}>
          <Form.Label>{vm.translateFilter("bankTransactionId")}</Form.Label>
          <AdvancedInput
            target={vm.filter}
            property="bankTransactionId"
            placeholder={vm.translatePlaceholder("bankTransactionId")}
            ignoredCharacters={regexNumbersChars}
          />
        </Form.Group>
      </div>
    </FilterView>
  );
});

export default PaymentBankTransferFilters;
